import { fromJS, List, Map } from 'immutable';
import { AUTHENTICATION_SUCCESS } from 'data/user';
import {
  success,
  error,
  loading,
  mergeGroups,
  upsertGroups,
  loadGroupContext,
  searchGroup,
  mergeGroupResults,
  updateGroupContext,
  setGroupContextSelection,
  assignContext,
  unassignContext,
  MAIN_CONTEXT,
} from 'reducers';
import {
  DELETE_PROPERTY_GROUP_SUCCESS,
  SAVE_PROPERTY_SUCCESS,
  DELETE_GROUP_PROPERTIES_SUCCESS,
  PROPERTY_SKIP_TRACE_SUCCESS,
} from 'data/property';
import { SAVE_LISTINGS_SUCCESS } from 'data/search';
import { SAVE_INVESTOR_GROUP_SUCCESS, REMOVE_INVESTOR_FROM_GROUP_SUCCESS } from 'data/investors';

import * as ActionType from './actions';
import * as PsCore from './psCoreActions';

const defaultAppendJob = fromJS({
  message: '',
  lines: [],
  total: 0,
  dncEnabled: true,
});

const defaultState = fromJS({
  appendJob: defaultAppendJob,
  error: null,
  loading: false,
  statuses: [],
  types: [],
  groups: [],
  contexts: {},
  // propstream-core extras for the open contact (notes/documents), and whose
  // they are (ATAI-388)
  extrasContactId: null,
  noteLoading: false,
  noteLoaded: false,
  noteSaving: false,
  note: '',
  documents: [],
});

// Loading a different contact first drops the previous one's note/documents.
// noteSaving goes too: that contact's save response will be dropped below.
const claimExtras = (state, contactId) =>
  PsCore.sameContactId(state.get('extrasContactId'), contactId)
    ? state
    : state
        .merge({ extrasContactId: contactId, note: '', noteLoaded: false, noteSaving: false })
        .set('documents', []);

const isStale = (state, action) =>
  !PsCore.sameContactId(state.get('extrasContactId'), action.contactId);

export default function reducer(state = defaultState, action) {
  const { response } = action;

  switch (action.type) {
    case PsCore.RESET_CONTACT_EXTRAS:
      return state
        .merge({ extrasContactId: null, note: '', noteLoaded: false, noteLoading: false })
        .set('documents', []);

    case PsCore.LOAD_CONTACT_NOTES:
      return claimExtras(state, action.contactId).merge({ noteLoading: true });

    case PsCore.LOAD_CONTACT_NOTES_DONE:
      if (isStale(state, action)) return state;
      return state.merge({ note: action.note, noteLoading: false, noteLoaded: true });

    case PsCore.SAVE_CONTACT_NOTE:
      return state.merge({ noteSaving: true });

    // A stale save is already persisted server-side; reopening that contact reloads it.
    case PsCore.SAVE_CONTACT_NOTE_DONE:
      if (isStale(state, action)) return state;
      return state.merge({ note: action.note, noteSaving: false });

    case PsCore.LOAD_CONTACT_DOCUMENTS:
      return claimExtras(state, action.contactId);

    case PsCore.LOAD_CONTACT_DOCUMENTS_DONE:
      if (isStale(state, action)) return state;
      // `set`, not `merge`: merge would turn the array into an Immutable List,
      // and the component reads `.length` / `.map` off a plain array.
      return state.set('documents', action.documents);

    case PsCore.UPLOAD_CONTACT_DOCUMENT:
      return state.merge({ documentUploading: true });

    case PsCore.UPLOAD_CONTACT_DOCUMENT_DONE:
      return action.documents
        ? state.set('documents', action.documents).merge({ documentUploading: false })
        : state.merge({ documentUploading: false });

    case PsCore.DELETE_CONTACT_DOCUMENT:
      return state.merge({ documentDeleting: true });

    case PsCore.DELETE_CONTACT_DOCUMENT_DONE:
      return action.documents
        ? state.set('documents', action.documents).merge({ documentDeleting: false })
        : state.merge({ documentDeleting: false });

    case AUTHENTICATION_SUCCESS:
      return mergeGroups(state, response.contactGroups).merge({
        types: response.contactTypes,
        statuses: response.contactStatuses,
      });

    case ActionType.LOAD_CONTACT:
    case ActionType.SAVE_CONTACT:
    case ActionType.DELETE_CONTACT_GROUP:
    case ActionType.IMPORT_CONTACTS:
    case ActionType.APPEND_CONTACTS:
    case ActionType.GET_APPEND_JOB:
    case ActionType.UPDATE_APPEND_JOB:
    case ActionType.UPDATE_CONTACT_GROUP:
    case ActionType.DELETE_GROUP_CONTEXT_CONTACT:
    case ActionType.SEARCH_GROUP_CONTEXT_CONTACT:
    case ActionType.SAVE_GROUP_CONTEXT_CONTACT:
    case ActionType.EXPORT_CONTACTS:
    case ActionType.GET_CONTACT_IDS:
      return loading(state);

    case ActionType.LOAD_CONTACT_ERROR:
    case ActionType.SAVE_CONTACT_ERROR:
    case ActionType.DELETE_CONTACT_GROUP_ERROR:
    case ActionType.IMPORT_CONTACTS_ERROR:
    case ActionType.APPEND_CONTACTS_ERROR:
    case ActionType.GET_APPEND_JOB_ERROR:
    case ActionType.UPDATE_APPEND_JOB_ERROR:
    case ActionType.UPDATE_CONTACT_GROUP_ERROR:
    case ActionType.DELETE_GROUP_CONTEXT_CONTACT_ERROR:
    case ActionType.SEARCH_GROUP_CONTEXT_CONTACT_ERROR:
    case ActionType.SAVE_GROUP_CONTEXT_CONTACT_ERROR:
    case ActionType.EXPORT_CONTACTS_ERROR:
    case ActionType.GET_CONTACT_IDS_ERROR:
      return error(state, action);

    case ActionType.LOAD_CONTACT_SUCCESS:
      return success(state.merge({ contact: response }));

    case ActionType.SEARCH_GROUP_CONTEXT_CACHED_CONTACT:
      return searchGroup(state, action.context);

    case ActionType.LOAD_GROUP_CONTEXT_CONTACT: {
      // Switching the main list to a different group: clear that group's cached selection before it becomes active.
      const { name, groupId } = action;
      const path = ['contexts', name, 'groups', groupId];
      const switching = name === MAIN_CONTEXT && state.getIn(path) && state.getIn(['contexts', name, 'group', 'id']) !== groupId;

      return loadGroupContext(!switching ? state : state.updateIn(path, c => c.merge({
        allSelected: false,
        remainingSelected: false,
        selected: 0,
        selection: List(),
        fullSelection: Map(),
        data: c.get('data').map(d => d.set('selected', false)),
      })), action);
    }

    case ActionType.SEARCH_GROUP_CONTEXT_CONTACT_SUCCESS:
      return success(mergeGroupResults(state, action.context, response));

    case ActionType.ASSIGN_CONTACTS_CHUNK: {
      const chunkIds = (action.payload && action.payload.chunkIds) || [];
      const chunkIdsSet = new Set(chunkIds);
      const assignedCtx = state.getIn(['contexts', 'assigned', 'group']);
      const availableCtx = state.getIn(['contexts', 'available', 'group']);
      const availableData = (availableCtx && availableCtx.get('data')) || fromJS([]);
      const chunkData = availableData
        .filter((d) => chunkIdsSet.has(d.get('id')))
        .map((item) => item.set('assigned', true)); // Mark as assigned here

      const newAvailableData = availableData.filter((d) => !chunkIdsSet.has(d.get('id')));

      const size =
        (assignedCtx && assignedCtx.get('size') ? assignedCtx.get('size') : 0) + chunkData.size;

      const afterAssigned = mergeGroupResults(state, assignedCtx, {
        data: chunkData,
        size,
        index: (assignedCtx && assignedCtx.get('size')) || 0,
      });

      const updatedAvailableCtx = (availableCtx || fromJS({})).merge({
        data: newAvailableData,
        selected: 0,
        selection: fromJS([]),
        remaining: Math.max(
          0,
          availableCtx && availableCtx.get('remaining')
            ? availableCtx.get('remaining') - chunkData.size
            : 0
        ),
      });

      const updatedState = afterAssigned.setIn(
        ['contexts', 'available', 'group'],
        updatedAvailableCtx
      );

      return success(updatedState);
    }

    case ActionType.ASSIGN_CONTACTS_DONE:
      return success(state);

    case ActionType.UPDATE_GROUP_CONTEXT_CONTACT:
      return updateGroupContext(state, action.context);

    case ActionType.SELECT_GROUP_CONTEXT_CONTACT:
      return setGroupContextSelection(state, action);

    case ActionType.DELETE_CONTACT_GROUP_SUCCESS:
    case ActionType.DELETE_GROUP_CONTEXT_CONTACT_SUCCESS:
    case ActionType.SAVE_GROUP_CONTEXT_CONTACT_SUCCESS:
    case ActionType.APPEND_CONTACTS_SUCCESS:
    case ActionType.UPDATE_APPEND_JOB_SUCCESS:
    case ActionType.UPDATE_CONTACT_GROUP_SUCCESS:
      return success(mergeGroups(state, response));

    case DELETE_PROPERTY_GROUP_SUCCESS:
    case DELETE_GROUP_PROPERTIES_SUCCESS:
    case SAVE_LISTINGS_SUCCESS:
    case SAVE_PROPERTY_SUCCESS:
      return success(mergeGroups(state, response.contactGroups));
    case PROPERTY_SKIP_TRACE_SUCCESS:
      return success(upsertGroups(state, response.skipTrace));

    // Investor List save returns only the affected group(s), not the full groups
    // list — upsert (not mergeGroups' full replace) so unaffected groups survive.
    case SAVE_INVESTOR_GROUP_SUCCESS:
      return success(upsertGroups(state, response));

    // ATAI-186: removeInvestorFromGroup hits the exact same generic
    // DELETE .../new endpoint as Contacts/index.js's drag-and-drop
    // (deleteGroupContext, below) — same full-groups-list response shape, so
    // mergeGroups (not upsertGroups, that's for the PUT .../investor-groups
    // response above, which returns only the affected group) is what keeps
    // the Contacts sidebar's Investor List count in sync when Remove is
    // triggered from the Search page/Profile modal's bookmark instead.
    case REMOVE_INVESTOR_FROM_GROUP_SUCCESS:
      return success(mergeGroups(state, response));

    case ActionType.ASSIGN_CONTACTS:
      return assignContext(state);

    case ActionType.UNASSIGN_CONTACTS:
      return unassignContext(state);

    case ActionType.SAVE_CONTACT_SUCCESS:
    case ActionType.IMPORT_CONTACTS_SUCCESS:
      return success(mergeGroups(state, response, true));

    case ActionType.GET_APPEND_JOB_SUCCESS:
      return success(state.merge({ appendJob: response }));

    case ActionType.EXPORT_CONTACTS_SUCCESS:
    case ActionType.GET_CONTACT_IDS_SUCCESS:
      return success(state);

    default:
      return state;
  }
}
